<template>
  <div>
    <div v-if="referralCode">
      <h3>Referral Code Detected</h3>
      <p>
        Thank you for using a referral code! Your code is:
        <strong>{{ referralCode }}</strong
        >. Sign up now and we'll send you $5 when your event is live and selling
        tickets!
      </p>
    </div>
    <div v-if="invitationError">
      <h3>Invitation Not Found</h3>
      <p>
        We're sorry, we can't find that invitation in our system. Our team has
        been notified that something went wrong. Please reach out to the person
        who sent you the invite.
      </p>
    </div>
    <div v-if="!invitationError">
      <h3 v-if="step === 1">
        {{ receivedInvite ? "Accept your invitation" : "Sign Up for Gobo" }}
      </h3>
      <h3 v-if="step === 2">Personalization</h3>
      <h3 v-if="step === 3">Details</h3>

      <div class="signup-stepper" v-if="step > 1">
        <p class="has-text-centered has-text-weight-semibold">
          Step {{ step - 1 }} of 2
        </p>
      </div>

      <div
        class="notification is-danger is-light"
        v-if="errorMessage"
        v-bind:class="{ 'm-fadeIn': errorMessage, 'm-fadeOut': !errorMessage }"
      >
        {{ errorMessage }}
      </div>
      <p class="overview" v-if="step === 1">
        {{ messages[step - 1] }}
      </p>
      <p class="disclaimer" v-if="step === 1">
        By signing up you agree to our
        <a target="_blank" href="https://www.gobo.show/terms-of-service"
          >terms of service</a
        >. We charge a 5% fee that includes credit card processing fees. For
        more information,
        <a target="_blank" href="https://www.gobo.show/why-gobo">click here</a>.
      </p>
    </div>
    <div v-show="!invitationError">
      <div v-show="step == 1">
        <div class="field">
          <label class="label">Name:</label>
          <div class="control has-icons-left">
            <input
              class="input is-primary"
              type="text"
              placeholder="Name"
              required
              v-model="user.name"
            />
            <span class="icon is-small is-left">
              <i class="fas fa-user"></i>
            </span>
          </div>
          <p class="help is-danger" v-if="nameChanged && !user.name">
            Please enter a name.
          </p>
        </div>
        <div class="field is-clearfix">
          <label class="label">My email is:</label>
          <div class="control has-icons-left">
            <input
              class="input is-primary"
              type="email"
              required
              placeholder="Email"
              v-model="user.email"
              v-if="!receivedInvite"
            />
            <input
              class="input is-primary"
              type="email"
              required
              placeholder="Email"
              v-model="receivedInvite.email"
              v-if="receivedInvite"
              disabled="true"
            />
            <span class="icon is-small is-left">
              <i class="fas fa-envelope"></i>
            </span>
          </div>
          <p class="help is-danger" v-if="!emailValid">
            Please enter a valid email address.
          </p>
        </div>
        <div class="field">
          <label class="label">Password:</label>
          <div class="control">
            <input
              class="input is-primary"
              type="password"
              placeholder="Password"
              required
              v-model="user.password"
            />
          </div>
        </div>
        <div class="field">
          <div class="control">
            <input
              class="input is-primary"
              type="password"
              placeholder="Confirm Password"
              required
              v-model="user.confirmPassword"
            />
          </div>
          <p class="help is-danger" v-if="!passwordsMatch">
            Passwords do not match.
          </p>
        </div>
      </div>

      <div v-show="step == 2" class="step-content">
        <h4 class="title is-5">Let's personalize your experience</h4>
        <p class="subtitle is-6">
          Choose the option that best fits your primary role so we can customize
          Gobo for your needs.
        </p>

        <div class="user-type-cards">
          <div
            class="card user-type-card"
            :class="{ 'is-selected': userType === 'venue' }"
            @click="userType = 'venue'"
          >
            <div class="card-content">
              <div class="media">
                <div class="media-left">
                  <span class="icon is-large">
                    <i class="fas fa-building fa-2x"></i>
                  </span>
                </div>
                <div class="media-content">
                  <p class="title is-6">Venue Manager</p>
                  <p class="subtitle is-7">
                    I manage a physical location that hosts performances
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            class="card user-type-card"
            :class="{ 'is-selected': userType === 'artist' }"
            @click="userType = 'artist'"
          >
            <div class="card-content">
              <div class="media">
                <div class="media-left">
                  <span class="icon is-large">
                    <i class="fas fa-music fa-2x"></i>
                  </span>
                </div>
                <div class="media-content">
                  <p class="title is-6">Artist/Performer</p>
                  <p class="subtitle is-7">
                    I'm a musician, comedian, theater performer, or entertainer
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            class="card user-type-card"
            :class="{ 'is-selected': userType === 'festival' }"
            @click="userType = 'festival'"
          >
            <div class="card-content">
              <div class="media">
                <div class="media-left">
                  <span class="icon is-large">
                    <i class="fas fa-calendar-alt fa-2x"></i>
                  </span>
                </div>
                <div class="media-content">
                  <p class="title is-6">Festival Organizer</p>
                  <p class="subtitle is-7">
                    I organize festivals or recurring events with multiple
                    artists
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            class="card user-type-card"
            :class="{ 'is-selected': userType === 'other' }"
            @click="userType = 'other'"
          >
            <div class="card-content">
              <div class="media">
                <div class="media-left">
                  <span class="icon is-large">
                    <i class="fas fa-users fa-2x"></i>
                  </span>
                </div>
                <div class="media-content">
                  <p class="title is-6">Other Event Organizer</p>
                  <p class="subtitle is-7">
                    I organize events that don't fit the above
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-show="step == 3" class="step-content">
        <div v-if="userType === 'venue'" class="venue-details">
          <h4 class="title is-5">Tell us about your venue</h4>

          <div class="field">
            <label class="label">Venue Location</label>
            <div class="control">
              <VenueFinder @venueFound="setVenueLocation" />
            </div>
            <p class="help">
              Start typing your venue's address to find it using Google Maps
            </p>
          </div>

          <div v-if="venueData.location" class="field">
            <label class="label">Venue Capacity</label>
            <div class="control">
              <input
                class="input"
                type="number"
                placeholder="Maximum number of people your venue can hold"
                v-model.number="venueData.capacity"
              />
            </div>
          </div>

          <div v-if="venueData.location" class="field">
            <label class="label">Venue Category</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="venueData.category">
                  <option value="">Select a category</option>
                  <option value="music">Music Venue</option>
                  <option value="performing-arts"
                    >Performing Arts Theater</option
                  >
                  <option value="comedy">Comedy Club</option>
                  <option value="multi-use">Multi-Use Space</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div v-if="userType === 'artist'" class="artist-details">
          <h4 class="title is-5">Tell us about your act</h4>

          <div class="field">
            <label class="label">Artist or Group Name</label>
            <div class="control">
              <input
                class="input"
                type="text"
                placeholder="Your stage name or band name"
                v-model="artistData.name"
              />
            </div>
          </div>

          <div class="field">
            <label class="label">Performance Category</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="artistData.category">
                  <option value="">Select a category</option>
                  <option value="music">Music</option>
                  <option value="performing-arts">Performing Arts</option>
                  <option value="comedy">Comedy</option>
                </select>
              </div>
            </div>
          </div>

          <div v-if="artistData.category === 'music'" class="field">
            <label class="label">Music Genre</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="artistData.genre">
                  <option value="">Select a genre</option>
                  <option value="rock">Rock</option>
                  <option value="pop">Pop</option>
                  <option value="jazz">Jazz</option>
                  <option value="electronic">Electronic</option>
                  <option value="hip-hop">Hip-Hop</option>
                  <option value="country">Country</option>
                  <option value="folk">Folk</option>
                  <option value="classical">Classical</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          <div v-if="artistData.category === 'performing-arts'" class="field">
            <label class="label">Performing Arts Genre</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="artistData.genre">
                  <option value="">Select a genre</option>
                  <option value="theater">Theater</option>
                  <option value="dance">Dance</option>
                  <option value="opera">Opera</option>
                  <option value="ballet">Ballet</option>
                  <option value="musical-theater">Musical Theater</option>
                  <option value="spoken-word">Spoken Word</option>
                  <option value="improv">Improv</option>
                  <option value="magic">Magic</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div v-if="userType === 'festival'" class="festival-details">
          <h4 class="title is-5">Tell us about your festival</h4>

          <div class="field">
            <label class="label">Festival Name</label>
            <div class="control">
              <input
                class="input"
                type="text"
                placeholder="Name of your festival or event series"
                v-model="festivalData.name"
              />
            </div>
          </div>

          <div class="field">
            <label class="label">Festival Location</label>
            <div class="control">
              <input
                class="input"
                type="text"
                placeholder="City, State/Province (e.g., Minneapolis, MN)"
                v-model="festivalData.location"
              />
            </div>
            <p class="help">Where does your festival typically take place?</p>
          </div>

          <div v-if="festivalData.location" class="field">
            <label class="label">Frequency</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="festivalData.frequency">
                  <option value="">How often does this festival happen?</option>
                  <option value="annual">Annual</option>
                  <option value="semi-annual"
                    >Semi-Annual (twice a year)</option
                  >
                  <option value="quarterly">Quarterly</option>
                  <option value="monthly">Monthly</option>
                  <option value="one-time">One-time event</option>
                  <option value="irregular">Irregular schedule</option>
                </select>
              </div>
            </div>
          </div>

          <div v-if="festivalData.location" class="field">
            <label class="label">Primary Focus</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="festivalData.primaryFocus">
                  <option value=""
                    >Select the primary focus of your festival</option
                  >
                  <option value="music">Music</option>
                  <option value="comedy">Comedy</option>
                  <option value="theater">Theater</option>
                  <option value="film">Film</option>
                  <option value="multi-genre">Multi-Genre</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div v-if="userType === 'other'" class="other-details">
          <h4 class="title is-5">Tell us about the events you organize</h4>

          <div class="field">
            <label class="label">Event Description</label>
            <div class="control">
              <textarea
                class="textarea"
                placeholder="Describe the types of events you create and organize"
                v-model="otherData.description"
                rows="4"
              ></textarea>
            </div>
            <p class="help">
              Help us understand what kinds of events you organize so we can
              better tailor Gobo to your needs.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="container cta" v-if="!invitationError">
      <div class="buttons" :class="step === 1 ? 'is-centered' : 'is-right'">
        <button class="button" @click="previousStep" v-if="step > 1">
          Back
        </button>
        <button
          class="button is-primary"
          @click="nextStep"
          :disabled="!canProceed"
        >
          {{ getButtonText() }}
        </button>
      </div>

      <div
        class="has-text-centered"
        style="margin-top: 15px;"
        v-if="step === 1"
      >
        <a class="primary" @click="$emit('switch-mode', 'login')"
          >Already have an account? Log in</a
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import "@/assets/variables.scss";
.m-fadeOut {
  visibility: hidden;
  opacity: 0;
  height: 0;
  padding-top: 0;
  padding-bottom: 0;
  transition: visibility 0s linear 600ms, opacity 600ms, height 600ms,
    padding-top 600ms;
}
.m-fadeIn {
  visibility: visible;
  opacity: 1;
  transition: visibility 0s linear 0s, opacity 600ms, height 600ms,
    padding-top 600ms, padding-bottom 600ms;
}
.disclaimer {
  font-size: 0.8em;
  line-height: 1.5em;
  color: $oops-red;
}
</style>

<script>
import firebase from "firebase/app";
import "firebase/firestore";
import store from "../store";
import { mapGetters } from "vuex";
import VenueFinder from "@/components/venue/venue-finder";

export default {
  name: "SignUp",
  components: {
    VenueFinder
  },
  props: {
    invitationId: String,
    invitationError: Boolean,
    referralCode: String
  },
  data() {
    return {
      step: 1,
      isValid: false,
      nameChanged: false,
      errorMessage: "",
      emailValid: true,
      passwordsMatch: true,
      messages: [
        "Sign up is easy and takes less than a minute.",
        "That's awesome. Tell us a little bit about yourself (you can fill out a full profile later).",
        "One last thing: how do you want to sign in?"
      ],
      user: {
        email: "",
        password: "",
        confirmPassword: "",
        name: "",
        type: ""
      },
      userType: "",
      venueData: {
        location: null,
        capacity: null,
        category: ""
      },
      artistData: {
        name: "",
        category: "",
        genre: ""
      },
      festivalData: {
        name: "",
        location: "",
        frequency: "",
        primaryFocus: ""
      },
      otherData: {
        description: ""
      }
    };
  },
  computed: {
    // map `this.user` to `this.$store.getters.user`
    ...mapGetters({
      receivedInvite: "receivedInvite"
    }),
    canProceed() {
      if (this.step === 1) {
        return (
          this.passwordsMatch &&
          this.user.confirmPassword &&
          this.user.password &&
          this.user.name &&
          ((this.emailValid && this.user.email) ||
            (this.receivedInvite && this.receivedInvite.email))
        );
      }
      if (this.step === 2) {
        return this.userType !== "";
      }
      if (this.step === 3) {
        switch (this.userType) {
          case "venue":
            return (
              this.venueData.location &&
              this.venueData.capacity &&
              this.venueData.category
            );
          case "artist":
            return (
              this.artistData.name &&
              this.artistData.category &&
              (!["music", "performing-arts"].includes(
                this.artistData.category
              ) ||
                this.artistData.genre)
            );
          case "festival":
            return (
              this.festivalData.name &&
              this.festivalData.location.trim() &&
              this.festivalData.frequency &&
              this.festivalData.primaryFocus
            );
          case "other":
            return this.otherData.description.trim().length > 0;
          default:
            return false;
        }
      }
      return false;
    }
  },
  async mounted() {
    if (this.invitationId) {
      console.log("yoyo");
      await store.dispatch("findInvite", this.invitationId);
      if (this.receivedInvite) this.user.email = this.receivedInvite.email;
      else this.invitationError = true;
    }
  },
  methods: {
    cancel() {
      this.$emit("canceled");
    },
    async nextStep() {
      if (this.step === 1) {
        await this.createAccount();
        this.step = 2;
      } else if (this.step === 2) {
        this.step = 3;
      } else if (this.step === 3) {
        await this.completeOnboarding();
        this.$emit("saved", this.user);
      }
    },
    previousStep() {
      if (this.step > 1) {
        this.step--;
      }
    },
    getButtonText() {
      if (this.step === 1) {
        return "Create Account";
      } else if (this.step === 2) {
        return "Next";
      } else {
        return "Get Started";
      }
    },
    setVenueLocation(venue) {
      this.venueData.location = venue;
    },
    validEmail(email) {
      //var re = new RegExp('/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/');
      if (email) return true; // re.test(email);
    },
    async createAccount() {
      try {
        // Create the account
        console.log("Authenticating", this.user.email);
        const email = this.receivedInvite
          ? this.receivedInvite.email
          : this.user.email;

        // create user
        await firebase
          .auth()
          .createUserWithEmailAndPassword(email, this.user.password)
          .then(function() {
            var user = firebase.auth().currentUser;
            console.log("We have user", user);
            user.sendEmailVerification();
          })
          .catch(function(error) {
            // Handle Errors here.
            var errorCode = error.code;
            var errorMessage = error.message;
            if (errorCode == "auth/weak-password") {
              alert("The password is too weak.");
            } else {
              alert(errorMessage);
            }
            console.log(error);
          });

        // update profile
        await firebase
          .auth()
          .currentUser.updateProfile({
            displayName: this.user.name
          })
          .then(function() {
            // Update successful.
            console.log("Profile updated");
          })
          .catch(function(error) {
            // An error happened.
            console.log(
              "Error updating profile " + error.code + ": " + error.errorMessage
            );
          });

        // Create basic user document
        var db = firebase.firestore();
        let newuser = {
          name: this.user.name,
          email: firebase.auth().currentUser.email,
          uid: firebase.auth().currentUser.uid
        };
        if (this.receivedInvite) newuser.invitationId = this.receivedInvite.id;
        if (this.referralCode) newuser.referredByCode = this.referralCode;

        await db
          .collection("users")
          .doc(firebase.auth().currentUser.uid)
          .set(newuser);

        // Clear any previous error messages
        this.errorMessage = "";
        console.log("Account created successfully");
      } catch (error) {
        console.error("Error creating account:", error);
        this.errorMessage =
          "There was an error creating your account. Please try again.";
        throw error;
      }
    },
    async completeOnboarding() {
      try {
        const db = firebase.firestore();
        const userRef = db
          .collection("users")
          .doc(firebase.auth().currentUser.uid);

        // Prepare the update data
        const updateData = {
          userType: this.userType,
          onboardingComplete: true,
          onboardingCompletedAt: firebase.firestore.FieldValue.serverTimestamp()
        };

        // Add type-specific flags for compatibility with existing system
        switch (this.userType) {
          case "venue":
            updateData.venueManager = true;
            break;
          case "artist":
            updateData.performingArtist = true;
            updateData.artistType = {
              name: this.artistData.name,
              category: this.artistData.category,
              genre: this.artistData.genre || null
            };
            break;
          case "festival":
            updateData.festivalManager = true;
            updateData.festivalType = {
              name: this.festivalData.name,
              location: this.festivalData.location,
              frequency: this.festivalData.frequency,
              primaryFocus: this.festivalData.primaryFocus
            };
            break;
          case "other":
            updateData.otherEventOrganizer = true;
            updateData.otherType = {
              description: this.otherData.description
            };
            break;
        }

        // Update user record first
        await userRef.update(updateData);

        // Fetch the updated user data to ensure Vuex store is in sync
        await this.$store.dispatch("fetchUser", firebase.auth().currentUser);

        // Now create profiles after user data is properly loaded in Vuex store
        switch (this.userType) {
          case "venue":
            // Save venue to venues collection using existing Vuex action
            await this.saveVenueUsingVuex();
            break;
          case "artist":
            // Create artist profile using existing Vuex action
            await this.createArtistProfile();
            break;
          case "festival":
            // Create festival profile using existing Vuex action
            await this.createFestivalProfile();
            break;
        }

        // For artists and venue managers, explicitly fetch their profiles to ensure they show up immediately
        if (this.userType === "artist") {
          await this.$store.dispatch(
            "fetchProfiles",
            firebase.auth().currentUser.uid
          );
        }
        if (this.userType === "venue") {
          await this.$store.dispatch(
            "fetchMyVenues",
            firebase.auth().currentUser.uid
          );
        }
        if (this.userType === "festival") {
          await this.$store.dispatch(
            "fetchMyFestivals",
            firebase.auth().currentUser.uid
          );
        }

        console.log("Onboarding completed successfully");
      } catch (error) {
        console.error("Error saving onboarding data:", error);
        this.errorMessage =
          "There was an error saving your information. Please try again.";
      }
    },
    async saveVenueUsingVuex() {
      try {
        const venue = this.venueData.location;
        const user = firebase.auth().currentUser;

        // Follow the same pattern as venue.vue - just add owner info and additional fields to the Google Maps object
        venue.owner = user.uid;
        venue.owners = {};
        venue.owners[user.uid] = { name: this.user.name };
        venue.capacity = this.venueData.capacity;
        venue.category = this.venueData.category;

        // Use the existing Vuex action to save the venue
        await this.$store.dispatch("addVenueProfile", venue);

        console.log("Venue saved using Vuex action with ID:", venue.place_id);
      } catch (error) {
        console.error("Error saving venue using Vuex:", error);
        throw error;
      }
    },
    async createArtistProfile() {
      try {
        const user = firebase.auth().currentUser;

        // First add a new profile placeholder (required for the Vuex workflow)
        await this.$store.dispatch("addNewProfile", user);

        // Create artist profile object in the format expected by the existing Vuex action
        const artistProfile = {
          name: this.artistData.name,
          type: this.artistData.category,
          genre: this.artistData.genre || null,
          links: {
            web: "",
            instagram: "",
            facebook: "",
            twitter: ""
          }
        };

        // Use the existing Vuex action to create the artist profile
        await this.$store.dispatch("createPerformerProfile", artistProfile);

        console.log("Artist profile created using Vuex action");
      } catch (error) {
        console.error("Error creating artist profile:", error);
        throw error;
      }
    },
    async createFestivalProfile() {
      try {
        const user = firebase.auth().currentUser;

        // Create festival profile object in the format expected by the existing Vuex action
        const festivalProfile = {
          name: this.festivalData.name,
          location: this.festivalData.location,
          frequency: this.festivalData.frequency,
          primaryFocus: this.festivalData.primaryFocus,
          owner: user.uid,
          owners: {
            [user.uid]: {
              name: this.user.name
            }
          },
          tax: "remit"
        };

        // Use the existing Vuex action to create the festival profile
        await this.$store.dispatch("createFestivalProfile", festivalProfile);

        console.log("Festival profile created using Vuex action");
      } catch (error) {
        console.error("Error creating festival profile:", error);
        throw error;
      }
    }
  },
  watch: {
    user: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue.name) this.nameChanged = true;
        if (newValue.password && newValue.confirmPassword) {
          this.passwordsMatch = newValue.password == newValue.confirmPassword;
        } else this.passwordsMatch = true;
        if (newValue.email) this.emailValid = this.validEmail(newValue.email);
        else this.emailValid = true;
        // Legacy isValid for backward compatibility
        this.isValid =
          this.passwordsMatch &&
          newValue.confirmPassword &&
          newValue.password &&
          ((this.emailValid && newValue.email) ||
            (this.receivedInvite && this.receivedInvite.email));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.cta {
  margin-top: 1.5em;
}

.signup-stepper {
  margin-bottom: 2rem;

  p {
    color: $active-green;
    font-size: 1rem;
  }
}

.step-content {
  margin-bottom: 1.5rem;
}

.user-type-cards {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
}

.user-type-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &.is-selected {
    border-color: $active-green;
    box-shadow: 0 4px 12px rgba($active-green, 0.3);
  }
}

.field {
  margin-bottom: 1.5rem;
}

.help {
  font-size: 0.875rem;
  color: $gray4;
}

.modal-inner-wrapper {
  margin-top: 0;
}

.card-content {
  padding: 1rem;
}
</style>
